import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import http from "../api/http";
import {
  AUTH_EXPIRED_EVENT,
  AUTH_TOKEN_KEY,
  clearStoredSession,
  getStoredToken,
  getStoredUser,
  storeSession,
} from "../api/authSession";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(getStoredToken);
  const [usuario, setUsuario] = useState(getStoredUser);
  const [verificando, setVerificando] = useState(() => Boolean(getStoredToken()));

  const limpiarSesion = useCallback(() => {
    clearStoredSession();
    setToken(null);
    setUsuario(null);
    setVerificando(false);
  }, []);

  useEffect(() => {
    const handleExpired = () => limpiarSesion();
    const handleStorage = (event) => {
      if (event.key !== AUTH_TOKEN_KEY) return;
      const storedToken = getStoredToken();
      if (!storedToken) {
        limpiarSesion();
        return;
      }
      setToken(storedToken);
      setUsuario(getStoredUser());
      setVerificando(true);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired);
      window.removeEventListener("storage", handleStorage);
    };
  }, [limpiarSesion]);

  useEffect(() => {
    let activo = true;

    const validarSesion = async () => {
      if (!token) {
        setVerificando(false);
        return;
      }

      try {
        const { data } = await http.get("/auth/me");
        if (!activo) return;
        if (data.usuario) {
          storeSession(token, data.usuario);
          setUsuario(data.usuario);
        }
      } catch {
        if (activo) limpiarSesion();
      } finally {
        if (activo) setVerificando(false);
      }
    };

    validarSesion();
    return () => {
      activo = false;
    };
  }, [token, limpiarSesion]);

  const guardarSesion = useCallback((data) => {
    if (!data.token || !data.usuario) throw new Error("Sesion incompleta.");
    storeSession(data.token, data.usuario);
    setToken(data.token);
    setUsuario(data.usuario);
    setVerificando(true);
  }, []);

  const login = useCallback(async ({ usuario: nombreUsuario, password }) => {
    limpiarSesion();
    const { data } = await http.post("/auth/login", { usuario: nombreUsuario, password });
    // El servidor decide si la politica de acceso requiere un segundo paso.
    if (data.token && data.usuario) {
      guardarSesion(data);
      return { authenticated: true };
    }
    if (!data.requiresWhatsappToken || !data.challengeId) {
      throw new Error("No se pudo iniciar la verificacion de WhatsApp.");
    }
    return data;
  }, [limpiarSesion, guardarSesion]);

  const verifyWhatsapp = useCallback(async ({ challengeId, code }) => {
    const { data } = await http.post("/auth/verify-whatsapp", { challengeId, code });
    guardarSesion(data);
  }, [guardarSesion]);

  const logout = limpiarSesion;

  const value = useMemo(
    () => ({
      token,
      usuario,
      autenticado: Boolean(token),
      verificando,
      login,
      verifyWhatsapp,
      logout,
    }),
    [token, usuario, verificando, login, verifyWhatsapp, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
